import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import StorageBar, { fmt } from '../components/ui/StorageBar';

/* ── Custom Dropdown ─────────────────────────────────────────────────────── */
const Dropdown = ({ value, onChange, options }) => {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, right: 0, width: 0 });
    const ref = useRef(null);
    const btnRef = useRef(null);
    useEffect(() => {
        const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);
    const handleToggle = () => {
        if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setCoords({ top: r.bottom + 4, right: window.innerWidth - r.right, width: r.width });
        }
        setOpen(o => !o);
    };
    const selected = options.find(o => o.value === value);
    return (
        <div ref={ref}>
            <button
                ref={btnRef}
                type="button"
                onClick={handleToggle}
                className="flex items-center justify-between gap-3 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white hover:border-[#895129] focus:outline-none transition min-w-[150px]"
            >
                <span className="text-slate-800 font-medium">{selected?.label ?? value}</span>
                <svg className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && (
                <div
                    style={{ position: 'fixed', top: coords.top, right: coords.right, minWidth: coords.width, zIndex: 9999 }}
                    className="bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden"
                >
                    {options.map(o => (
                        <button
                            key={o.value}
                            type="button"
                            onClick={() => { onChange(o.value); setOpen(false); }}
                            className={`w-full text-left px-3 py-2.5 text-sm transition flex items-center justify-between gap-2
                                ${value === o.value
                                    ? 'bg-[#895129]/10 text-[#895129] font-semibold'
                                    : 'text-slate-600 hover:bg-slate-50'}`}
                        >
                            {o.label}
                            {value === o.value && (
                                <svg className="w-3.5 h-3.5 text-[#895129] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

/* ── Inline status message ───────────────────────────────────────────────── */
const StatusMsg = ({ msg }) => {
    if (!msg) return null;
    return (
        <span className={`text-xs font-medium ${msg.ok ? 'text-emerald-600' : 'text-red-500'}`}>
            {msg.ok ? '✓ ' : '✗ '}{msg.text}
        </span>
    );
};

/* ── Section card ────────────────────────────────────────────────────────── */
const Section = ({ icon, title, subtitle, accent = 'indigo', children }) => {
    const accents = {
        indigo: 'border-[#8A6E29] bg-[#8A6E29]/10 text-[#8A6E29]',
        violet: 'border-[#8A6E29] bg-[#8A6E29]/10 text-[#8A6E29]',
        emerald: 'border-emerald-500 bg-emerald-50 text-emerald-600',
        amber: 'border-amber-500 bg-amber-50 text-amber-600',
        gray: 'border-slate-400 bg-slate-50 text-slate-600',
    };
    const [iconBg, , iconText] = (accents[accent] ?? accents.indigo).split(' ');
    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-start gap-4 px-4 py-4 sm:px-6 sm:py-5 border-b border-slate-100">
                {icon && (
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg} bg-opacity-60`}>
                        <span className={iconText}>{icon}</span>
                    </div>
                )}
                <div>
                    <h2 className="text-sm font-bold text-slate-800">{title}</h2>
                    {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
                </div>
            </div>
            <div className="px-4 py-4 sm:px-6 sm:py-5">{children}</div>
        </div>
    );
};

/* ── Row ─────────────────────────────────────────────────────────────────── */
const Row = ({ label, children }) => (
    <div className="flex items-center justify-between gap-2 flex-wrap text-sm py-1.5">
        <span className="text-slate-500">{label}</span>
        <span className="font-medium text-slate-800">{children}</span>
    </div>
);

/* ── Divider ─────────────────────────────────────────────────────────────── */
const Divider = () => <div className="border-t border-slate-100 my-5" />;

/* ── Generate random MinIO secret ────────────────────────────────────────── */
const genSecret = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    return Array.from(crypto.getRandomValues(new Uint8Array(24)))
        .map(b => chars[b % chars.length]).join('');
};

/* ════════════════════════════════════════════════════════════════════════════
   SettingsPage
   ════════════════════════════════════════════════════════════════════════════ */
const SettingsPage = ({ token, user, onLogout, addNotification, simplified = false, onSimplifiedChange }) => {
    /* quota */
    const [quotaInput, setQuotaInput] = useState('');
    const [quotaSaving, setQuotaSaving] = useState(false);
    const [localQuotaBytes, setLocalQuotaBytes] = useState(null); // tracks saved value locally

    /* cold threshold */
    const [coldDays, setColdDays] = useState(user?.cold_threshold_days ?? 90);
    const [coldSaving, setColdSaving] = useState(false);
    const [coldMsg, setColdMsg] = useState(null);

    /* change password */
    const [currentPw, setCurrentPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [pwSaving, setPwSaving] = useState(false);
    const [showCurrentPw, setShowCurrentPw] = useState(false);
    const [showNewPw, setShowNewPw] = useState(false);

    /* account actions */
    const [quotaReqSent, setQuotaReqSent] = useState(false);
    const [quotaRequestGB, setQuotaRequestGB] = useState('');
    const [pendingQuotaRequest, setPendingQuotaRequest] = useState(null);
    const [deleteExpanded, setDeleteExpanded] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    /* linked devices */
    const [devices, setDevices] = useState([]);
    const [devicesLoading, setDevicesLoading] = useState(true);
    const [removingDeviceId, setRemovingDeviceId] = useState(null);

    /* hardware endpoints */
    const [endpoints, setEndpoints] = useState([]);
    const [showEasySetup, setShowEasySetup] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [minioSecret] = useState(() => genSecret());
    const [endpointForm, setEndpointForm] = useState({
        endpoint_url: '', bucket: 'homeserver', access_key: 'homeserver',
        secret_key: '', gb_cap: '200',
    });
    const [endpointSaving, setEndpointSaving] = useState(false);
    const [endpointMsg, setEndpointMsg] = useState(null);
    const [removingId, setRemovingId] = useState(null);

    /* ── derived ── */
    /* ── storage refresh ── */
    const [refreshedUser, setRefreshedUser] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const effectiveUser = refreshedUser ?? user;

    const refreshStorage = async () => {
        setRefreshing(true);
        try {
            const u = await api.getProfile();
            setRefreshedUser(u);
            addNotification?.({ type: 'success', title: 'Storage refreshed', message: `${fmt(u.storage_used_bytes ?? 0)} used` });
        } catch {
            addNotification?.({ type: 'error', title: 'Refresh failed', message: 'Could not fetch latest storage data.' });
        } finally { setRefreshing(false); }
    };

    /* ── derived ── */
    const usedBytes = effectiveUser?.storage_used_bytes ?? 0;
    const maxBytes = effectiveUser?.quota_max_bytes ?? 0;
    const quotaBytes = effectiveUser?.storage_quota_bytes ?? 0;
    const quotaMaxGB = maxBytes > 0 ? Math.floor(maxBytes / (1024 ** 3)) : null;
    const quotaCurrentGB = quotaBytes > 0 ? Math.floor(quotaBytes / (1024 ** 3)) : (quotaMaxGB ?? 0);
    const planAssigned = maxBytes > 0;
    // Show the locally-saved value in the bar immediately after save, without waiting for a profile refetch
    const effectiveQuotaBytes = localQuotaBytes ?? (quotaBytes > 0 ? quotaBytes : maxBytes);

    // Cold threshold cutoff date
    const coldCutoffDate = new Date();
    coldCutoffDate.setDate(coldCutoffDate.getDate() - coldDays);
    const coldCutoffLabel = coldCutoffDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

    /* ── data loading ── */
    useEffect(() => {
        const defaultGB = quotaCurrentGB > 0 ? quotaCurrentGB : (quotaMaxGB ?? 0);
        if (defaultGB > 0) setQuotaInput(String(defaultGB));
    }, [quotaCurrentGB, quotaMaxGB]);

    const loadEndpoints = useCallback(() => {
        api.listStorageEndpoints()
            .then(data => setEndpoints(data.endpoints || []))
            .catch(() => { });
    }, [token]);

    const loadDevices = useCallback(() => {
        setDevicesLoading(true);
        api.listDevices()
            .then(data => setDevices(data.devices || []))
            .catch(() => { })
            .finally(() => setDevicesLoading(false));
    }, [token]);

    const removeDevice = async (id) => {
        setRemovingDeviceId(id);
        try {
            await api.deleteDevice(id);
            setDevices(prev => prev.filter(d => d.id !== id));
            addNotification?.({ type: 'success', title: 'Device removed', message: 'The device has been unlinked from your account.' });
        } catch {
            addNotification?.({ type: 'error', title: 'Failed to remove device', message: 'Could not unlink the device. Please try again.' });
        } finally { setRemovingDeviceId(null); }
    };

    useEffect(() => { loadEndpoints(); }, [loadEndpoints]);
    useEffect(() => { loadDevices(); }, [loadDevices]);

    // Load pending quota request for non-admin users
    useEffect(() => {
        if (user?.role === 'admin') return;
        api.getPendingQuotaRequest()
            .then(data => { if (data.request) setPendingQuotaRequest(data.request); })
            .catch(() => {});
    }, [token, user?.role]);

    /* ── quota save ── */
    const saveQuota = async () => {
        const gb = parseFloat(quotaInput);
        if (isNaN(gb) || gb <= 0) return;
        const bytes = Math.floor(gb * 1024 ** 3);
        setQuotaSaving(true);
        try {
            await api.updateQuota(bytes);
            setLocalQuotaBytes(bytes);
            addNotification?.({ type: 'success', title: 'Quota updated', message: `Active quota set to ${gb} GB.` });
        } catch {
            addNotification?.({ type: 'error', title: 'Quota update failed', message: 'Could not update your storage quota.' });
        }
        finally { setQuotaSaving(false); }
    };

    /* ── cold threshold save (auto-fires on dropdown change) ── */
    const saveColdThreshold = async (days) => {
        setColdDays(days);
        setColdSaving(true); setColdMsg(null);
        try {
            await api.updateSettings({ cold_threshold_days: days });
            setColdMsg({ ok: true, text: 'Saved.' });
            addNotification?.({ type: 'success', title: 'Cold storage updated', message: `Files move to cold storage after ${days} days.` });
        } catch {
            addNotification?.({ type: 'error', title: 'Settings save failed', message: 'Could not update cold storage threshold.' });
        }
        finally { setColdSaving(false); }
    };

    /* ── endpoint add ── */
    const addEndpoint = async (e) => {
        e.preventDefault();
        setEndpointSaving(true); setEndpointMsg(null);
        try {
            await api.addStorageEndpoint({
                ...endpointForm,
                endpoint_url: endpointForm.endpoint_url || 'http://localhost:9000',
                gb_cap: parseInt(endpointForm.gb_cap, 10),
            });
            setEndpointForm({ endpoint_url: '', bucket: 'homeserver', access_key: 'homeserver', secret_key: '', gb_cap: '200' });
            setEndpointMsg({ ok: true, text: 'Connected. Health check running…' });
            setShowEasySetup(false); setShowAdvanced(false);
            loadEndpoints();
            addNotification?.({ type: 'success', title: 'Storage endpoint connected', message: endpointForm.endpoint_url || 'localhost:9000' });
        } catch {
            setEndpointMsg({ ok: false, text: 'Failed to connect.' });
            addNotification?.({ type: 'error', title: 'Endpoint connection failed', message: 'Could not connect to the storage endpoint.' });
        }
        finally { setEndpointSaving(false); }
    };

    /* ── endpoint remove ── */
    const removeEndpoint = async (id) => {
        if (!window.confirm('Remove this storage endpoint?')) return;
        setRemovingId(id);
        try {
            await api.removeStorageEndpoint(id);
            setEndpoints(prev => prev.filter(ep => ep.id !== id));
            addNotification?.({ type: 'info', title: 'Storage endpoint removed', message: 'The endpoint has been disconnected.' });
        } catch { alert('Failed to remove.'); }
        finally { setRemovingId(null); }
    };

    /* ── MinIO install command ── */
    const secret = endpointForm.secret_key || minioSecret;
    const minioCmd =
        `docker run -d --name minio --restart unless-stopped \\\n` +
        `  -p 9000:9000 -p 9001:9001 \\\n` +
        `  -e MINIO_ROOT_USER=homeserver \\\n` +
        `  -e MINIO_ROOT_PASSWORD=${secret} \\\n` +
        `  -v ~/minio-data:/data \\\n` +
        `  minio/minio server /data --console-address :9001`;

    const prefillEasyForm = () => {
        setEndpointForm(f => ({
            ...f,
            endpoint_url: 'http://localhost:9000',
            bucket: 'homeserver',
            access_key: 'homeserver',
            secret_key: secret,
        }));
    };

    return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f9f4f0' }}>
            {/* Header */}
            <header className="text-white shadow-lg" style={{ background: 'linear-gradient(to right, #660F09, #895129)' }}>
                <div className="max-w-3xl mx-auto px-3 py-3 sm:px-6 sm:py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </Link>
                        <div>
                            <h1 className="text-lg font-bold">Settings</h1>
                            <p className="text-xs" style={{ color: '#AD7A32' }}>{user?.email}</p>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl w-full mx-auto px-3 py-5 sm:px-6 sm:py-8 space-y-4">

                {/* ── Account ──────────────────────────────────────────── */}
                <Section
                    icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
                    title="Account"
                    accent="indigo"
                >
                    <Row label="Username">{user?.username}</Row>
                    <Row label="Email">{user?.email}</Row>
                    <Row label="Role">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide
                            ${user?.role === 'admin' ? 'bg-[#8A6E29]/15 text-[#8A6E29]' : 'bg-slate-100 text-slate-600'}`}>
                            {user?.role}
                        </span>
                    </Row>
                    <Row label="Plan">
                        {user?.plan_tier ? (
                            <span className="px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700">
                                {user.plan_tier}
                            </span>
                        ) : (
                            <span className="text-xs text-slate-400 italic">Not assigned</span>
                        )}
                    </Row>
                </Section>

                {/* ── Change Password ──────────────────────────────────── */}
                <Section
                    icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>}
                    title="Change Password"
                    subtitle="Update your account password"
                    accent="indigo"
                >
                    <form onSubmit={async (e) => {
                        e.preventDefault();
                        if (newPw !== confirmPw) {
                            addNotification?.({ type: 'error', title: 'Mismatch', message: 'New passwords do not match.' });
                            return;
                        }
                        if (newPw.length < 6) {
                            addNotification?.({ type: 'error', title: 'Too short', message: 'Password must be at least 6 characters.' });
                            return;
                        }
                        setPwSaving(true);
                        try {
                            await api.changePassword(currentPw, newPw);
                            setCurrentPw(''); setNewPw(''); setConfirmPw('');
                            addNotification?.({ type: 'success', title: 'Password updated', message: 'Your password has been changed.' });
                        } catch (err) {
                            addNotification?.({ type: 'error', title: 'Failed', message: err.message || 'Could not change password.' });
                        } finally { setPwSaving(false); }
                    }} className="space-y-3">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-slate-500">Current password</label>
                            <div className="relative">
                                <input
                                    type={showCurrentPw ? 'text' : 'password'}
                                    value={currentPw}
                                    onChange={e => setCurrentPw(e.target.value)}
                                    className="w-full px-3 py-2 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#AD7A32]"
                                    required
                                />
                                <button type="button" onClick={() => setShowCurrentPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                    {showCurrentPw
                                        ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                        : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                    }
                                </button>
                            </div>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-slate-500">New password</label>
                            <div className="relative">
                                <input
                                    type={showNewPw ? 'text' : 'password'}
                                    value={newPw}
                                    onChange={e => setNewPw(e.target.value)}
                                    className="w-full px-3 py-2 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#AD7A32]"
                                    minLength={6}
                                    required
                                />
                                <button type="button" onClick={() => setShowNewPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                    {showNewPw
                                        ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                        : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                    }
                                </button>
                            </div>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-slate-500">Confirm new password</label>
                            <input
                                type="password"
                                value={confirmPw}
                                onChange={e => setConfirmPw(e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#AD7A32] ${confirmPw && confirmPw !== newPw ? 'border-red-300' : 'border-slate-200'}`}
                                minLength={6}
                                required
                            />
                            {confirmPw && confirmPw !== newPw && (
                                <span className="text-xs text-red-500">Passwords do not match</span>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={pwSaving || !currentPw || !newPw || newPw !== confirmPw}
                            className="px-4 py-2 text-sm font-semibold text-white bg-[#895129] hover:bg-[#660F09] rounded-lg transition disabled:opacity-40"
                        >
                            {pwSaving ? 'Updating...' : 'Update password'}
                        </button>
                    </form>
                </Section>

                {/* ── Storage ──────────────────────────────────────────── */}
                <Section
                    icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" /></svg>}
                    title="Storage"
                    subtitle="Your usage and active quota"
                    accent="violet"
                >
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Current usage</span>
                        <button
                            onClick={refreshStorage}
                            disabled={refreshing}
                            className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border border-slate-200 text-slate-500 hover:border-[#895129] hover:text-[#895129] transition disabled:opacity-40"
                        >
                            <svg className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            {refreshing ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                    <StorageBar used={usedBytes} quota={effectiveQuotaBytes} max={maxBytes} />
                    <Row label="Plan ceiling">
                        {maxBytes > 0
                            ? <span className="font-semibold text-slate-800">{fmt(maxBytes)}</span>
                            : <span className="text-xs text-slate-400 italic">Not assigned</span>}
                    </Row>

                    <Divider />
                    <p className="text-sm font-semibold text-slate-700 mb-3">Active quota</p>

                    {planAssigned ? (
                        <div className="space-y-3">
                            <div className="flex items-center gap-4">
                                <input
                                    type="range"
                                    min={Math.max(1, Math.ceil(usedBytes / (1024 ** 3)))}
                                    max={quotaMaxGB}
                                    step={1}
                                    value={quotaInput || quotaCurrentGB || 0}
                                    onChange={e => setQuotaInput(e.target.value)}
                                    className="flex-1 accent-[#895129]"
                                />
                                <div className="flex items-center gap-1.5">
                                    <input
                                        type="number"
                                        min={Math.max(1, Math.ceil(usedBytes / (1024 ** 3)))}
                                        max={quotaMaxGB}
                                        value={quotaInput}
                                        onChange={e => setQuotaInput(e.target.value)}
                                        className="w-20 px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#AD7A32]"
                                    />
                                    <span className="text-sm text-slate-400">GB</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={saveQuota}
                                    disabled={quotaSaving}
                                    className="px-4 py-1.5 bg-[#895129] hover:bg-[#660F09] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition"
                                >
                                    {quotaSaving ? 'Saving…' : 'Save quota'}
                                </button>
                                <span className="text-xs text-slate-400">Plan ceiling: {fmt(maxBytes)}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="flex items-center gap-4">
                                <input
                                    type="range"
                                    min={Math.max(1, Math.ceil(usedBytes / (1024 ** 3)))}
                                    max={1000}
                                    step={1}
                                    value={quotaInput || quotaCurrentGB || 0}
                                    onChange={e => setQuotaInput(e.target.value)}
                                    className="flex-1 accent-[#895129]"
                                />
                                <div className="flex items-center gap-1.5">
                                    <input
                                        type="number"
                                        min={Math.max(1, Math.ceil(usedBytes / (1024 ** 3)))}
                                        value={quotaInput}
                                        onChange={e => setQuotaInput(e.target.value)}
                                        placeholder="—"
                                        className="w-20 px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#AD7A32]"
                                    />
                                    <span className="text-sm text-slate-400">GB</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={saveQuota}
                                    disabled={quotaSaving || !quotaInput}
                                    className="px-4 py-1.5 bg-[#895129] hover:bg-[#660F09] disabled:opacity-40 text-white text-sm font-semibold rounded-lg transition"
                                >
                                    {quotaSaving ? 'Saving…' : 'Save quota'}
                                </button>
                            </div>
                        </div>
                    )}
                </Section>

                {/* ── Cold Storage Threshold ────────────────────────────── */}
                <Section
                    icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m8.66-9h-1M4.34 12h-1m14.95 5.66l-.71-.71M6.34 6.34l-.71-.71m12.02 0l-.71.71M6.34 17.66l-.71.71M12 7a5 5 0 100 10A5 5 0 0012 7z" /></svg>}
                    title="Cold Storage"
                    subtitle="Files not opened for this many days move to cheaper storage automatically"
                    accent="amber"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-700 font-medium">Move files after</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                                {coldSaving
                                    ? 'Saving…'
                                    : <span className="font-semibold text-slate-600">{coldDays} days</span>
                                }
                            </p>
                        </div>
                        <Dropdown
                            value={coldDays}
                            onChange={v => saveColdThreshold(v)}
                            options={[
                                { value: 30, label: '30 days' },
                                { value: 60, label: '60 days' },
                                { value: 90, label: '90 days (default)' },
                                { value: 180, label: '6 months' },
                                { value: 365, label: '1 year' },
                            ]}
                        />
                    </div>
                </Section>

                {/* ── Display ──────────────────────────────────────────── */}
                <Section
                    icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}
                    title="Display"
                    subtitle="Personalise how information is shown in the file browser"
                    accent="indigo"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-700 font-medium">Storage location labels</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                                {simplified
                                    ? <><span className="font-semibold text-slate-600">Simplified</span> — Cloud &amp; Local</>
                                    : <><span className="font-semibold text-slate-600">Technical</span> — B2, MinIO, Local, Agent</>
                                }
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => onSimplifiedChange?.(!simplified)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#AD7A32] focus:ring-offset-2 ${simplified ? 'bg-[#895129]' : 'bg-slate-200'}`}
                        >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${simplified ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                    </div>
                </Section>

                {/* ── Linked Devices ───────────────────────────────────── */}
                <Section
                    icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>}
                    title="Linked Devices"
                    subtitle="Devices connected to your account. Each login from a new device registers it here."
                    accent="amber"
                >
                    {devicesLoading ? (
                        <div className="flex items-center justify-center py-6">
                            <div className="w-5 h-5 border-2 border-[#895129] border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : devices.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">No devices linked yet. Log in from a mobile or desktop app to see them here.</p>
                    ) : (
                        <div className="space-y-2">
                            {devices.map(device => {
                                const typeIcons = {
                                    ios: '📱', android: '📱', macos: '💻', windows: '💻', linux: '🖥️', web: '🌐',
                                };
                                const lastSeen = device.last_seen_at
                                    ? new Date(device.last_seen_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                    : 'Never';
                                return (
                                    <div key={device.id} className="flex items-center gap-3 p-3.5 rounded-xl border border-slate-100 bg-slate-50">
                                        <span className="text-lg flex-shrink-0">{typeIcons[device.device_type] || '📟'}</span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-slate-800 truncate">{device.device_name}</p>
                                            <p className="text-xs text-slate-400">
                                                {device.device_type} · Last seen {lastSeen}
                                                {device.app_version ? ` · v${device.app_version}` : ''}
                                            </p>
                                        </div>
                                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                            device.last_seen_at && (Date.now() - new Date(device.last_seen_at).getTime()) < 5 * 60 * 1000
                                                ? 'bg-emerald-500'
                                                : device.last_seen_at && (Date.now() - new Date(device.last_seen_at).getTime()) < 6 * 60 * 60 * 1000
                                                    ? 'bg-amber-400'
                                                    : 'bg-slate-300'
                                        }`} title={
                                            device.last_seen_at && (Date.now() - new Date(device.last_seen_at).getTime()) < 5 * 60 * 1000
                                                ? 'Online'
                                                : device.last_seen_at && (Date.now() - new Date(device.last_seen_at).getTime()) < 6 * 60 * 60 * 1000
                                                    ? 'Recently active'
                                                    : 'Offline'
                                        } />
                                        <button
                                            onClick={() => removeDevice(device.id)}
                                            disabled={removingDeviceId === device.id}
                                            className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                                            title="Remove device"
                                        >
                                            {removingDeviceId === device.id
                                                ? <div className="w-4 h-4 border-b border-red-400 rounded-full animate-spin" />
                                                : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            }
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Section>

                {/* ── My Hardware ──────────────────────────────────────── */}
                <Section
                    icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" /></svg>}
                    title="My Hardware"
                    subtitle="Use your own device as cold storage. Old files are routed here instead of paid cloud."
                    accent="emerald"
                >
                    <a
                        href="/intro"
                        className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:border-emerald-400 hover:bg-emerald-100 transition text-left group"
                    >
                        <div className="w-10 h-10 bg-emerald-500 group-hover:bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0 transition">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-bold text-emerald-800">Setup Guide</p>
                            <p className="text-xs text-emerald-600 mt-0.5">Learn how to connect your own hardware as cold storage.</p>
                        </div>
                        <svg className="w-4 h-4 text-emerald-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                    </a>
                </Section>

                {/* ── Connected Endpoints (kept for users who already have hardware) ── */}
                {endpoints.length > 0 && <Section
                    icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
                    title="Connected Endpoints"
                    subtitle="Your currently connected hardware storage endpoints."
                    accent="emerald"
                >
                    <div className="space-y-2 mb-6">
                        {endpoints.map(ep => (
                                <div key={ep.id} className="flex items-center gap-3 p-3.5 rounded-xl border border-slate-100 bg-slate-50">
                                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${ep.is_healthy ? 'bg-emerald-500' : ep.is_active ? 'bg-amber-400 animate-pulse' : 'bg-red-400'}`} />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-slate-800 truncate">{ep.endpoint_url || 'localhost:9000'}</p>
                                        <p className="text-xs text-slate-400">{ep.bucket} · {ep.gb_cap} GB cap</p>
                                    </div>
                                    <span className={`text-xs font-medium ${ep.is_healthy ? 'text-emerald-600' : ep.is_active ? 'text-amber-600' : 'text-red-500'}`}>
                                        {ep.is_healthy ? 'Connected' : ep.is_active ? 'Checking…' : 'Offline'}
                                    </span>
                                    <button
                                        onClick={() => removeEndpoint(ep.id)}
                                        disabled={removingId === ep.id}
                                        className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                                        title="Remove"
                                    >
                                        {removingId === ep.id
                                            ? <div className="w-4 h-4 border-b border-red-400 rounded-full animate-spin" />
                                            : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        }
                                    </button>
                                </div>
                            ))}
                        </div>
                </Section>}

                {/* ── Account Actions ─────────────────────────────────── */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-6 py-5 space-y-5">

                        {/* Request quota upgrade — hidden for admins (they manage quotas directly) */}
                        {user?.role !== 'admin' && <div className="space-y-3">
                            <div>
                                <p className="text-sm font-semibold text-slate-800">Request storage upgrade</p>
                                <p className="text-xs text-slate-400 mt-0.5">Ask an admin to raise your quota ceiling</p>
                            </div>
                            {pendingQuotaRequest ? (
                                <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
                                    <span className="text-xs font-semibold text-amber-700">Pending request: {(pendingQuotaRequest.requested_bytes / (1024 ** 3)).toFixed(1)} GB</span>
                                </div>
                            ) : quotaReqSent ? (
                                <span className="text-xs font-semibold text-emerald-600">Request sent</span>
                            ) : (
                                <form onSubmit={async (e) => {
                                    e.preventDefault();
                                    const gb = parseFloat(quotaRequestGB);
                                    if (!gb || gb <= 0) return;
                                    setQuotaReqSent(true);
                                    try {
                                        await api.requestQuotaUpgrade(Math.floor(gb * 1024 ** 3));
                                        addNotification?.({ type: 'success', title: 'Quota upgrade requested', message: 'An admin will review your request.' });
                                    } catch (e) {
                                        setQuotaReqSent(false);
                                        addNotification?.({ type: 'error', title: 'Request failed', message: e.message || 'Could not send upgrade request.' });
                                    }
                                }} className="flex items-end gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Desired quota (GB)</label>
                                        <input
                                            type="number"
                                            min="1"
                                            step="1"
                                            value={quotaRequestGB}
                                            onChange={e => setQuotaRequestGB(e.target.value)}
                                            placeholder="e.g. 500"
                                            className="w-32 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#AD7A32]"
                                            required
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border rounded-lg transition"
                                        style={{ color: '#895129', borderColor: 'rgba(137,81,41,0.3)', backgroundColor: 'rgba(137,81,41,0.07)' }}
                                        onMouseEnter={e => e.currentTarget.style.backgroundColor='rgba(137,81,41,0.12)'}
                                        onMouseLeave={e => e.currentTarget.style.backgroundColor='rgba(137,81,41,0.07)'}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                                        </svg>
                                        Request upgrade
                                    </button>
                                </form>
                            )}
                        </div>}

                        {/* Danger zone */}
                        <div className="border-t-2 border-red-200 pt-5 mt-2">
                            <div className="flex items-center gap-2 mb-4">
                                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                </svg>
                                <p className="text-xs font-bold text-red-500 uppercase tracking-widest">Danger zone</p>
                            </div>
                            <div className="rounded-xl border-2 border-red-200 bg-red-50/50 p-4">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <p className="text-sm font-bold text-red-700">Delete account</p>
                                        <p className="text-xs text-red-500/70 mt-0.5">Permanently remove your account and all files. This cannot be undone.</p>
                                    </div>
                                    <button
                                        onClick={() => setDeleteExpanded(e => !e)}
                                        className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-red-600 border-2 border-red-300 bg-white hover:bg-red-600 hover:text-white hover:border-red-600 rounded-lg transition"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Delete my account
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

            </main>

            {/* ── Delete account modal ── */}
            {deleteExpanded && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => { setDeleteExpanded(false); setDeleteConfirmText(''); }} />
                    <div className="relative bg-white rounded-2xl shadow-2xl border border-red-200 w-full max-w-md p-6 sm:p-8">
                        <div className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-red-100 mb-5">
                            <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-bold text-slate-800 text-center">Schedule account deletion?</h3>
                        <p className="text-sm text-slate-500 text-center mt-2 leading-relaxed">
                            Your account will be scheduled for <strong className="text-red-600">permanent deletion in 30 days</strong>. During this period you will be logged out and unable to log in. Only an admin can cancel the deletion. After 30 days, all files and data will be permanently removed.
                        </p>
                        <div className="mt-5">
                            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                Type <span className="font-mono bg-red-50 text-red-600 px-1.5 py-0.5 rounded">delete{user?.username}permanently</span> to confirm
                            </label>
                            <input
                                type="text"
                                value={deleteConfirmText}
                                onChange={e => setDeleteConfirmText(e.target.value)}
                                placeholder={`delete${user?.username}permanently`}
                                className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition"
                                autoComplete="off"
                                spellCheck={false}
                            />
                        </div>
                        <div className="mt-5 space-y-3">
                            <button
                                disabled={deleteConfirmText !== `delete${user?.username}permanently`}
                                onClick={async () => {
                                    try {
                                        const data = await api.deleteMyAccount();
                                        const date = data.scheduled_deletion_at
                                            ? new Date(data.scheduled_deletion_at).toLocaleDateString()
                                            : '30 days from now';
                                        addNotification({ type: 'warning', message: `Account scheduled for deletion on ${date}. Contact an admin to cancel.` });
                                        onLogout();
                                    } catch (err) {
                                        addNotification({ type: 'error', message: err.message || 'Failed to schedule account deletion.' });
                                    }
                                }}
                                className="w-full px-5 py-3 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed rounded-xl transition shadow-sm"
                            >
                                Schedule account deletion
                            </button>
                            <button
                                onClick={() => { setDeleteExpanded(false); setDeleteConfirmText(''); }}
                                className="w-full px-5 py-3 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SettingsPage;
